import { useState, useEffect } from "react";
import { usePageTitle } from "@/hooks/use-page-title";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save } from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface SystemSettings {
  default_tax_name?: string | null;
  default_tax_rate?: string | null;
  email_sender_name?: string | null;
  support_email?: string | null;
  business_mailing_address?: string | null;
}

async function adminFetch(path: string, opts?: RequestInit) {
  const res = await fetch(`${BASE}/api/admin${path}`, {
    ...opts,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...opts?.headers },
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`);
  return data;
}

export default function AdminSettings() {
  usePageTitle("Admin – Settings");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const [defaultTaxName, setDefaultTaxName] = useState("");
  const [defaultTaxRate, setDefaultTaxRate] = useState("");
  const [emailSenderName, setEmailSenderName] = useState("");
  const [supportEmail, setSupportEmail] = useState("");
  const [mailingAddress, setMailingAddress] = useState("");

  useEffect(() => {
    adminFetch("/system-settings")
      .then((data: SystemSettings) => {
        setDefaultTaxName(data.default_tax_name ?? "");
        setDefaultTaxRate(data.default_tax_rate ?? "");
        setEmailSenderName(data.email_sender_name ?? "");
        setSupportEmail(data.support_email ?? "");
        setMailingAddress(data.business_mailing_address ?? "");
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setSaving(true);
    try {
      await adminFetch("/system-settings", {
        method: "PUT",
        body: JSON.stringify({
          default_tax_name: defaultTaxName,
          default_tax_rate: defaultTaxRate,
          email_sender_name: emailSenderName,
          support_email: supportEmail,
          business_mailing_address: mailingAddress,
        }),
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "200px" }}>
        <Loader2 style={{ width: "1.5rem", height: "1.5rem", animation: "spin 1s linear infinite" }} />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: "2rem" }}>
        <h1 style={{ fontSize: "1.75rem", fontWeight: 700, marginBottom: "0.25rem" }}>System Settings</h1>
        <p style={{ color: "hsl(var(--muted-foreground))", fontSize: "0.9375rem" }}>
          Platform-wide default configuration
        </p>
      </div>

      <form onSubmit={handleSave}>
        <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>

          <Card>
            <CardHeader>
              <CardTitle style={{ fontSize: "1.0625rem" }}>Tax Defaults</CardTitle>
              <CardDescription>Global defaults applied to new estimates and invoices.</CardDescription>
            </CardHeader>
            <CardContent style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div>
                <Label htmlFor="default-tax-name">Default Tax Name</Label>
                <Input
                  id="default-tax-name"
                  value={defaultTaxName}
                  onChange={(e) => setDefaultTaxName(e.target.value)}
                  placeholder="e.g. Sales Tax, VAT"
                  style={{ marginTop: "0.375rem" }}
                />
              </div>
              <div>
                <Label htmlFor="default-tax-rate">Default Tax Rate (%)</Label>
                <Input
                  id="default-tax-rate"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={defaultTaxRate}
                  onChange={(e) => setDefaultTaxRate(e.target.value)}
                  placeholder="e.g. 8.5"
                  style={{ marginTop: "0.375rem" }}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle style={{ fontSize: "1.0625rem" }}>Email Configuration</CardTitle>
              <CardDescription>Settings for platform-generated emails.</CardDescription>
            </CardHeader>
            <CardContent style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div>
                <Label htmlFor="email-sender-name">Email Sender Name</Label>
                <Input
                  id="email-sender-name"
                  value={emailSenderName}
                  onChange={(e) => setEmailSenderName(e.target.value)}
                  placeholder="e.g. QuoTrak Team"
                  style={{ marginTop: "0.375rem" }}
                />
              </div>
              <div>
                <Label htmlFor="support-email">Support Email Address</Label>
                <Input
                  id="support-email"
                  type="email"
                  value={supportEmail}
                  onChange={(e) => setSupportEmail(e.target.value)}
                  placeholder="support@example.com"
                  style={{ marginTop: "0.375rem" }}
                />
              </div>
              <div>
                <Label htmlFor="business-mailing-address">Business Mailing Address</Label>
                <Input
                  id="business-mailing-address"
                  value={mailingAddress}
                  onChange={(e) => setMailingAddress(e.target.value)}
                  placeholder="Street or PO box, city, province, postal code"
                  style={{ marginTop: "0.375rem" }}
                />
                <p style={{ marginTop: "0.375rem", fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>
                  Shown at the bottom of announcements sent from Email Users.
                </p>
              </div>
            </CardContent>
          </Card>

          {error && (
            <div style={{ padding: "0.75rem 1rem", background: "hsl(var(--destructive) / 0.1)", borderRadius: "0.5rem", color: "hsl(var(--destructive))", fontSize: "0.875rem" }}>
              {error}
            </div>
          )}

          {success && (
            <div style={{ padding: "0.75rem 1rem", background: "hsl(142, 76%, 36%, 0.1)", borderRadius: "0.5rem", color: "hsl(142, 76%, 36%)", fontSize: "0.875rem" }}>
              Settings saved successfully.
            </div>
          )}

          <div>
            <Button type="submit" disabled={saving}>
              {saving
                ? <Loader2 style={{ width: "1rem", height: "1rem", marginRight: "0.5rem", animation: "spin 1s linear infinite" }} />
                : <Save style={{ width: "1rem", height: "1rem", marginRight: "0.5rem" }} />
              }
              Save Settings
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
